import { useScrollContent } from '@exam-management/common/hooks';
import { useItemSet } from '@exam-management/common/item-set-provider';
import { useConfig } from '@exam-management/configuration';
import { QuestionGroupType, useCreateQuestionMutation } from '@exam-management/graphql';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useParams } from '@exam-management/router';
import { Button, Flex, Tooltip } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { DEFAULT_FORM_VALUES } from './constants';
import type { CreateItemSetSubQuestionFormInput } from './types';

export default function ItemSetSubQuestionCreationFormActions() {
  const { showSnackbar } = useSnackbar();
  const { scrollTo } = useScrollContent();
  const configuration = useConfig();

  const { itemSet } = useItemSet();
  const { structureId } = useParams(
    '/qbank-structures/:structureId/qcontents/item-set/:questionId',
  );

  const formContext = useFormContext<CreateItemSetSubQuestionFormInput>();

  const { formState } = formContext;
  const { isSubmitSuccessful } = formState;

  const [create, { loading: isLoading }] = useCreateQuestionMutation({
    onCompleted: () => {
      showSnackbar({ message: 'Question has been saved', variant: 'success' });
      scrollTo({ x: 0, y: 0 });
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to save question.',
        variant: 'error',
      });
    },
    update: (cache, { data }) => {
      if (data?.qctCreateQuestion) {
        const newQuestion = data.qctCreateQuestion;
        cache.modify({
          id: `QuestionGroup:${itemSet.id}`,
          fields: {
            questions(existingQuestions = [], { toReference }) {
              const newQuestionRef = toReference(newQuestion);
              return [...existingQuestions, newQuestionRef];
            },
          },
        });
      }
    },
  });

  const createSubQuestion = async (data: CreateItemSetSubQuestionFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;

    if (!qBankStructureLevelItemId) return null;

    const isCalculated =
      data.information.calculated === '' ? null : data.information.calculated === 'true';

    const group = {
      id: itemSet.id,
      type: QuestionGroupType.ItemSet,
    };

    const pValue =
      data.information.pValue.selected === 'pInitial'
        ? data.information.pValue.pInitial
        : data.information.pValue.pStat;

    const cognitiveDomains = data.properties.cognitiveDomain.map((domain) => domain.value);

    const convertedData = {
      details: parseContentForApi(data.detail.details),
      choices: data.detail.choices.map((choice) => ({
        ...choice,
        label: parseContentForApi(choice.label),
      })),
      correctAnswer: data.detail.correctAnswer,
      calculated: isCalculated,
      pValue: typeof pValue === 'number' ? pValue : undefined, // NOTE: pValue can be number, empty string, or undefined
      cognitiveDomains,
      learningObjectiveStatement: data.properties.learningObjectiveStatement?.value ?? undefined,
      solution: parseContentForApi(data.properties.solution),
      reference: parseContentForApi(data.properties.reference),
      group,
      ...data.owners,
    };

    const result = await create({
      variables: {
        input: {
          qBankStructureLevelItemId,
          refQBankStructureId: structureId,
          data: convertedData,
        },
      },
    });

    return result.data?.qctCreateQuestion.id;
  };

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleSaveAndCreateNew = async () => {
    await formContext.handleSubmit(async (data) => {
      await createSubQuestion(data);
    }, handleShowRequiredFieldsError)();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      const location = formContext.getValues('location');
      formContext.reset({
        ...DEFAULT_FORM_VALUES,
        location,
      });
    }
  }, [isSubmitSuccessful]);

  const maxSubQuestions = configuration.modules.qcontent.maxSubQuestions;

  const isWillReachMaxSubQuestions = (itemSet.questions?.length ?? 0) >= maxSubQuestions - 1;

  return (
    <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
      <Button
        type='submit'
        disabled={formContext.formState.isSubmitting}
        // NOTE: This is a workaround to show loading status for only one button
        loading={formContext.formState.isSubmitting && !isLoading}
      >
        Save
      </Button>
      <Tooltip
        placement='top'
        arrow
        title={
          isWillReachMaxSubQuestions ? 'ไม่สามารถเพิ่ม Sub Question ได้อีก (สูงสุด 5 ข้อ)' : ''
        }
      >
        <Button
          type='button'
          color='secondary'
          variant='outlined'
          disabled={formContext.formState.isSubmitting || isWillReachMaxSubQuestions}
          loading={isLoading}
          onClick={handleSaveAndCreateNew}
          sx={(theme) => ({
            '&.Mui-disabled': {
              pointerEvents: 'auto',
              bgcolor: theme.palette.grey[100],
              ':hover': {
                borderColor: theme.palette.grey['300'],
              },
            },
          })}
        >
          Save & Create New
        </Button>
      </Tooltip>
    </Flex>
  );
}
